import { Button } from '@react-spectrum/s2';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import { useBridgewayApi } from '@/api/BridgewayApi';
import { getInstagramChannelFromCalendars } from '@/api/ExpressApi';
import { useExpressApiContext } from '@/api/ExpressApiContext';
import { useUserId } from '@/auth/UserIdContext';
import {
	TYPE_TITLES,
	isArtifactActionable,
} from '@/components/artifact/panelMeta';
import { PostReview } from '@/components/artifact/PostReview';
import { resolveReferenceDroppedNotice } from '@/components/artifact/referenceDroppedNotice';
import { useEnsureSetupSnapshot } from '@/components/artifact/useEnsureSetupSnapshot';
import { useGoToNextStep } from '@/components/artifact/useGoToNextStep';
import { useStepSubmitted } from '@/components/artifact/useStepSubmitted';
import { BridgewayGlyph } from '@/components/BridgewayGlyph';
import { useIsGeneratingImages } from '@/hooks/useIsGeneratingImages';
import { useOpenChatPanel } from '@/hooks/useOpenChatPanel';
import { useUserSettings } from '@/hooks/useUserSettings';
import { logger } from '@/logger';
import { ChatPanelLayout } from '@/spaces/chat/panels/ChatPanelLayout';
import {
	scheduledDeckNeedsResync,
	syncScheduledExpressPost,
} from '@/spaces/chat/panels/expressPostSync';
import { SuccessInterstitial } from '@/spaces/chat/panels/SuccessInterstitial';
import { trackEvent } from '@/utils/fullstory';

import type { InstagramPost } from '../../../../../bridge/types/artifact.ts';

interface PostArtifactPanelProps {
	artifact: InstagramPost;
	onBack?: () => void;
	onClose: () => void;
	style?: React.CSSProperties;
}

function normalizeSchedule(
	schedule: Record<string, unknown> | null | undefined,
): { fire_at?: string } | undefined {
	if (!schedule || typeof schedule !== 'object') {
		return undefined;
	}
	return typeof schedule.fire_at === 'string'
		? { fire_at: schedule.fire_at }
		: undefined;
}

export const PostArtifactPanel = ({
	artifact,
	onBack,
	onClose,
	style,
}: PostArtifactPanelProps) => {
	const bridgeApi = useBridgeApi();
	const bridgewayApi = useBridgewayApi();
	const userId = useUserId();
	const { expressApi } = useExpressApiContext();
	const { settings } = useUserSettings();
	const openChatPanel = useOpenChatPanel();
	const goToNextItem = useGoToNextStep();
	useEnsureSetupSnapshot(artifact.process_id);
	const [savedInterstitialOpen, setSavedInterstitialOpen] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);
	const slideCount = Math.max(
		artifact.content.image_urls?.length ?? 0,
		artifact.content.slide_prompts?.length ?? 0,
		1,
	);
	const isCarousel = slideCount > 1;
	const { submittedView, markSubmitted } = useStepSubmitted({
		title: isCarousel
			? 'Instagram carousel confirmed'
			: 'Instagram post confirmed',
		summary: 'Ready for the next step in your campaign setup.',
		onClose,
		onGoToNextStep: () => {
			if (artifact.process_id) goToNextItem(artifact.process_id, artifact.id);
		},
	});

	const handleContentChange = useCallback(
		(updates: Record<string, unknown>) => {
			bridgeApi.sendAction({
				type: 'do_edit_artifact',
				artifact_id: artifact.id,
				edits: updates,
			});
		},
		[bridgeApi, artifact.id],
	);

	const handleRetry = useCallback(() => {
		const slidePrompts = artifact.content.slide_prompts ?? [];
		// Never shrink the carousel: a legacy artifact may carry image_urls with
		// no persisted slide_prompts, so derive the count from the widest signal
		// available (prompts, already-generated images, or the original requested
		// count) — otherwise a retry collapses a multi-slide carousel to one post.
		const count = Math.max(
			slidePrompts.length,
			artifact.content.image_urls?.length ?? 0,
			artifact.content.image_generation?.requested_count ?? 0,
			1,
		);
		bridgeApi.sendAction({
			type: 'do_generate_post_images_for_artifact',
			artifact_id: artifact.id,
			slide_count: count,
			slide_prompts: slidePrompts,
		});
	}, [
		bridgeApi,
		artifact.id,
		artifact.content.slide_prompts,
		artifact.content.image_urls,
		artifact.content.image_generation,
	]);

	const currentChatId = bridgeApi.user_state?.current_chat_id ?? null;
	const currentChat = currentChatId
		? (bridgeApi.user_state?.chats?.[currentChatId] ?? null)
		: null;
	const isStandaloneEdit =
		currentChat?.linked_item?.type === 'artifact' &&
		currentChat.linked_item.id === artifact.id;

	const handleSubmit = useCallback(() => {
		if (!artifact.process_id) return;
		trackEvent('Post Submitted', { artifact_id: artifact.id });
		bridgeApi.sendAction({
			type: 'do_mark_setup_item_completed',
			process_id: artifact.process_id,
			item_id: artifact.id,
		});
		if (isStandaloneEdit) {
			openChatPanel({
				panel_type: 'process_schedule',
				item_id: artifact.process_id,
			});
			return;
		}
		markSubmitted();
	}, [
		bridgeApi,
		openChatPanel,
		artifact.id,
		artifact.process_id,
		isStandaloneEdit,
		markSubmitted,
	]);

	// A post that is already scheduled lives in Express's scheduler, which
	// publishes from media it already holds — content edits persisted to the
	// doc never reach it on their own, so Save must also push them to the
	// scheduled Express post.
	const isScheduledOnExpress =
		artifact.status === 'scheduled' && !!artifact.express_metadata?.post_id;

	const handleSave = useCallback(async () => {
		setSaveError(null);
		bridgeApi.sendAction({
			type: 'do_save_artifact',
			artifact_id: artifact.id,
		});
		if (!isScheduledOnExpress) {
			setSavedInterstitialOpen(true);
			return;
		}
		setIsSaving(true);
		try {
			const channelInfo = getInstagramChannelFromCalendars(
				settings?.express_connection?.calendar,
			);
			if (!expressApi || !channelInfo || !userId) {
				throw new Error('Missing Express connection details');
			}
			await syncScheduledExpressPost({
				expressApi,
				channelInfo,
				bridgewayApi,
				userId,
				artifact,
			});
			setSavedInterstitialOpen(true);
		} catch (error) {
			logger.error('Failed to update the scheduled Express post on save', {
				artifact_id: artifact.id,
				error: error instanceof Error ? error.message : String(error),
			});
			setSaveError('Saved, but updating the scheduled post failed. Try again.');
		} finally {
			setIsSaving(false);
		}
	}, [
		artifact,
		bridgeApi,
		bridgewayApi,
		expressApi,
		isScheduledOnExpress,
		settings,
		userId,
	]);

	const aspectRatio = isCarousel ? '4 / 5' : '1 / 1';
	const title = isCarousel
		? 'Instagram Carousel'
		: (TYPE_TITLES[artifact.type] ?? 'Review Item');

	const hasGenerationError = !!artifact.content.last_generation_error;
	// Retry runs a WHOLE regeneration, which rewrites surviving_slide_indices.
	// A redraw in flight was planned against the old list, so its
	// compare-and-swap then refuses and the repaired slide is thrown away. The
	// filmstrip and the empty-state photo picker already wait on this exact
	// condition (PostReview.tsx), and the chat tools refuse a structural edit
	// while a redraw renders (services/py/chat/delegation_tools.py, edit_in_flight).
	// Neither reaches here: the branches below replace PostReview entirely, so
	// the Retry controls are the one slide-destroying control left ungated.
	let hasOutstandingRedraw = false;
	for (const req of Object.values(
		bridgeApi.user_state?.outstanding_requests ?? {},
	)) {
		if (
			req?.type === 'request_redraw_slide' &&
			req.artifact_id === artifact.id
		) {
			hasOutstandingRedraw = true;
		}
	}
	// Carry a repaired slide through to the scheduler. A redraw writes the
	// document and cannot reach Express from the bridge, so an already-scheduled
	// post keeps publishing the deck it uploaded before the repair. Gated on a
	// real divergence, so an unchanged post makes no request, and keyed on the
	// deck so one repair is synced once. uploadSlidesToExpress reuses the cached
	// attachment id for every unchanged slide, so only the repaired slide is
	// uploaded again.
	const deckKey = (artifact.content.image_urls ?? []).join('\n');
	const resyncedDeckRef = useRef<string | null>(null);
	const needsResync = scheduledDeckNeedsResync(artifact);
	useEffect(() => {
		if (!needsResync || hasOutstandingRedraw || isSaving) return;
		if (resyncedDeckRef.current === deckKey) return;
		const channelInfo = getInstagramChannelFromCalendars(
			settings?.express_connection?.calendar,
		);
		if (!expressApi || !channelInfo || !userId) return;
		resyncedDeckRef.current = deckKey;
		void syncScheduledExpressPost({
			expressApi,
			channelInfo,
			bridgewayApi,
			userId,
			artifact,
		}).catch((error) => {
			// The deck stays marked as attempted. Clearing it here would let the
			// next render call again, and `artifact` changes identity on any state
			// update, so a failing Express call would retry in a loop. Reopening
			// the panel gets a fresh ref and tries once more.
			logger.error('Failed to carry a repaired slide to the scheduled post', {
				artifact_id: artifact.id,
				error: error instanceof Error ? error.message : String(error),
			});
		});
	}, [
		artifact,
		bridgewayApi,
		deckKey,
		expressApi,
		hasOutstandingRedraw,
		isSaving,
		needsResync,
		settings,
		userId,
	]);

	const isGenerating = useIsGeneratingImages(artifact);
	const imageGeneration = artifact.content.image_generation;
	// The record can claim fewer slides than the post actually holds. A later
	// total failure overwrites the record with a delivered count of zero while
	// the document still carries the slides an earlier run produced, and the run
	// after that clears the error, so the panel reaches the shortfall banner
	// beside a slide the customer can see. Count what the document holds.
	//
	// Only the generated slides count. A photo the customer adds grows
	// image_urls and leaves image_generation alone, so a raw deck count reads
	// their own picture as a slide the image service delivered, and the
	// shortfall notice and its Retry control both disappear. A missing entry
	// means "generated", the same answer the bridge helpers pad with.
	const generatedInDocument = (artifact.content.image_urls ?? []).reduce(
		(count, _url, index) =>
			artifact.content.ai_generated_slides?.[index] === false
				? count
				: count + 1,
		0,
	);
	const deliveredCount = Math.max(
		imageGeneration?.delivered_count ?? 0,
		generatedInDocument,
	);
	const isPartial =
		!!imageGeneration?.partial &&
		imageGeneration.requested_count > deliveredCount;
	// A slide can be dropped for transient reasons (retrying may succeed) or
	// because the image service refused the description itself — the
	// content-safety check or a validation rejection (retrying the same prompt
	// won't). Only the latter needs the "reword or retrying won't help" copy.
	const failedReasons = imageGeneration?.failed_reasons ?? [];
	const blockedByContentSafety =
		isPartial && failedReasons.includes('content_safety');
	const rejectedAsInvalid =
		isPartial && failedReasons.includes('invalid_prompt');
	const refusedAsTooBusy = isPartial && failedReasons.includes('rate_limited');
	const noImageReturned = isPartial && failedReasons.includes('no_image');
	// Every slide of every attempt failed, so no carousel came back at all and the
	// partial banner below never renders. The error branch is the only surface
	// left to tell the customer the descriptions themselves were refused.
	// last_generation_error and image_generation are written by different code
	// paths, so the block can survive from an older attempt. Name a cause only
	// when the block itself reports that nothing was delivered — a block holding
	// a delivered slide describes some earlier attempt, not this failure.
	//
	// The raw count is read here, not the widened one above: this describes the
	// attempt that just failed, and every failure site writes an empty picture
	// list beside a full list of failed positions.
	const totalFailureDeliveredNothing =
		hasGenerationError &&
		imageGeneration?.delivered_count === 0 &&
		(imageGeneration.failed_indices?.length ?? 0) > 0;
	// Content safety and a validation rejection both mean the stored wording is
	// refused every time it is sent, so both need the reword copy. handleRetry
	// re-sends the same slide_prompts, so a plain Retry here is a control that
	// cannot succeed. They are ranked the same way the partial banner ranks
	// them: content safety names the strictest thing the customer has to
	// change, so it wins when a carousel lost slides to both.
	const totalFailureWasContentSafety =
		totalFailureDeliveredNothing && failedReasons.includes('content_safety');
	const totalFailureWasInvalidPrompt =
		totalFailureDeliveredNothing &&
		!failedReasons.includes('content_safety') &&
		failedReasons.includes('invalid_prompt');
	const totalFailureNeedsReword =
		totalFailureWasContentSafety || totalFailureWasInvalidPrompt;
	const slideCounts = `(Generated ${deliveredCount} of ${imageGeneration?.requested_count} slides.)`;
	// One carousel can lose slides to several causes at once, so exactly one
	// message has to win. A cause a plain retry cannot clear goes first, so the
	// customer hears it even when a busy service or a missing image also cost
	// them a slide. Content safety outranks the rest: it names the strictest
	// thing the customer has to change.
	const partialGenerationMessage = !isPartial
		? null
		: blockedByContentSafety
			? `Some slides were blocked by the content-safety check. Reword those slide descriptions or use a different reference photo — retrying unchanged won't help. ${slideCounts}`
			: rejectedAsInvalid
				? `Some slides were turned down because the image service couldn't accept those slide descriptions. Reword those slides — retrying unchanged won't help. ${slideCounts}`
				: refusedAsTooBusy
					? `The image service was busy and turned some slides away. Retrying usually works. ${slideCounts}`
					: noImageReturned
						? `Some slides didn't come back from the image service. Retrying usually works. ${slideCounts}`
						: `Generated ${deliveredCount} of ${imageGeneration?.requested_count} slides.`;
	// ADO-1738: suppressed while partial because the partial banner above
	// already covers that case and wins the one notice slot.
	const referenceDroppedMessage = resolveReferenceDroppedNotice(
		artifact.content,
		isPartial,
	);

	const snapshot = artifact.process_id
		? (bridgeApi.user_state?.process_setup_snapshots?.[artifact.process_id] ??
			null)
		: null;
	const isStandalone = (snapshot?.children.length ?? 0) === 1;
	const showStandaloneActions = isStandalone || isStandaloneEdit;
	const showSubmitButton =
		showStandaloneActions || isArtifactActionable(artifact);
	const actionRenderer =
		showSubmitButton && !savedInterstitialOpen && !submittedView
			? () => (
					<div className="ml-auto flex items-center gap-2">
						{saveError && (
							<span className="text-sm text-red-700">{saveError}</span>
						)}
						{showStandaloneActions && (
							<Button
								type="button"
								variant="secondary"
								onPress={handleSave}
								isPending={isSaving}
								isDisabled={isGenerating || isSaving}
							>
								Save
							</Button>
						)}
						<Button
							type="button"
							variant="accent"
							onPress={handleSubmit}
							isDisabled={isGenerating || isSaving}
						>
							{showStandaloneActions ? 'Schedule' : 'Approve'}
						</Button>
					</div>
				)
			: undefined;

	return (
		<ChatPanelLayout
			title={savedInterstitialOpen ? 'Saved' : title}
			actionRenderer={actionRenderer}
			onBack={onBack}
			onClose={onClose}
			style={style}
		>
			{submittedView ??
				(savedInterstitialOpen ? (
					<SuccessInterstitial
						title="Saved"
						summary={
							isScheduledOnExpress
								? 'Your changes are saved and your scheduled post is updated.'
								: 'Your draft is saved. You can publish or schedule it anytime.'
						}
						secondaryAction={{ label: 'Close', onPress: onClose }}
						primaryAction={{
							label: 'Publish',
							onPress: () => {
								setSavedInterstitialOpen(false);
								handleSubmit();
							},
						}}
					/>
				) : isGenerating ? (
					<div className="flex w-full flex-col">
						<div
							className="[container-type:inline-size] relative w-full
								overflow-x-hidden"
							style={{ height: 'min(62vh, 620px)' }}
						>
							<div className="flex h-full items-center gap-3">
								<div
									aria-hidden
									className="h-full flex-shrink-0"
									style={{
										width: `calc(50cqw - ${
											isCarousel ? 'min(24.8vh, 248px)' : 'min(31vh, 310px)'
										})`,
									}}
								/>
								{Array.from({ length: slideCount }).map((_, i) => (
									<div
										key={i}
										className={`relative h-full flex-shrink-0 transition-opacity
											duration-300 ${i === 0 ? 'opacity-100' : 'opacity-30'}`}
										style={{ aspectRatio }}
									>
										<div
											className="flex h-full w-full animate-pulse items-center
												justify-center overflow-hidden rounded-2xl bg-gray-100"
										>
											<BridgewayGlyph />
										</div>
									</div>
								))}
								<div
									aria-hidden
									className="h-full flex-shrink-0"
									style={{ width: '50cqw' }}
								/>
							</div>
						</div>
						{isCarousel && (
							<div
								className="flex justify-center gap-2 px-4 pt-2"
								aria-hidden="true"
							>
								{Array.from({ length: slideCount }).map((_, i) => (
									<div
										key={i}
										className={`size-2 rounded-full ${
											i === 0 ? 'bg-(--text-primary)' : 'bg-(--text-muted)/40'
											}`}
									/>
								))}
							</div>
						)}
						<div className="flex flex-col items-center gap-1 px-4 py-3">
							<span className="text-sm text-gray-700">
								{isCarousel
									? `Generating ${slideCount} slides…`
									: 'Generating…'}
							</span>
						</div>
					</div>
				) : hasGenerationError ? (
					<div className="flex flex-col items-center gap-3 px-4 py-8">
						<p className="text-sm text-red-700">
							{totalFailureWasContentSafety
								? "The content-safety check blocked these slide descriptions. Reword them or use a different reference photo — retrying unchanged won't help."
								: totalFailureWasInvalidPrompt
									? "The image service couldn't accept these slide descriptions. Reword them — retrying unchanged won't help."
									: "Couldn't generate the carousel images."}
						</p>
						{hasOutstandingRedraw && (
							<p className="text-sm text-gray-700">
								Redrawing a slide now — this takes a minute or so. Retry works
								again once it lands.
							</p>
						)}
						<Button
							variant="secondary"
							onPress={handleRetry}
							isDisabled={hasOutstandingRedraw}
						>
							{totalFailureNeedsReword ? 'Retry anyway' : 'Retry'}
						</Button>
					</div>
				) : (
					<div className="flex flex-col">
						{partialGenerationMessage && (
							<div
								className="mx-4 mt-3 flex items-center justify-between gap-3
									rounded-xl border border-amber-200 bg-amber-50 px-3 py-2
									text-sm text-amber-900"
							>
								<span>
									{partialGenerationMessage}
									{hasOutstandingRedraw &&
										' Redrawing a slide now — this takes a minute or so.'}
								</span>
								<Button
									variant="secondary"
									onPress={handleRetry}
									isDisabled={hasOutstandingRedraw}
								>
									{blockedByContentSafety || rejectedAsInvalid
										? 'Retry anyway'
										: 'Retry'}
								</Button>
							</div>
						)}
						{referenceDroppedMessage && (
							// No Retry button: regeneration carries no library photo handle, so
							// it would drop the photo again and the button would promise
							// something it cannot deliver.
							<div
								className="mx-4 mt-3 flex items-center justify-between gap-3
									rounded-xl border border-amber-200 bg-amber-50 px-3 py-2
									text-sm text-amber-900"
							>
								<span>{referenceDroppedMessage}</span>
							</div>
						)}
						<PostReview
							artifactId={artifact.id}
							content={artifact.content}
							schedule={normalizeSchedule(artifact.schedule)}
							onContentChange={handleContentChange}
						/>
					</div>
				))}
		</ChatPanelLayout>
	);
};
